/**
 * ConversationFiltersPanel — the Conversations tab's filter panel (UI-SPEC §2.15), on B's
 * `FacetSection`: Persona, Intention de départ, Thème / Cas / Critère, Lieu, Langue, Tags,
 * counted in conversations, each facet's counts applying every other facet.
 */
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ConversationPersona } from '@traaker/shared';
import { FacetSection } from '../FiltersPanel';
import { CONV_FACETS, convFacetCounts, convFilterCount } from './conversationModel';
import type { ConvFacet, ConvFacetValues, ConvFilter } from './conversationModel';

export interface ConversationFiltersPanelProps {
  personas: ConversationPersona[];
  q: string;
  filter: ConvFilter;
  onChange: (f: ConvFilter) => void;
  values: (p: ConversationPersona) => ConvFacetValues;
  searchText: (p: ConversationPersona) => string;
  /** A facet value's label (persona name, intent, term, place, language, tag). */
  labelOf: (facet: ConvFacet, value: string) => string;
  onClose: () => void;
}

export function ConversationFiltersPanel({ personas, q, filter, onChange, values, searchText, labelOf, onClose }: ConversationFiltersPanelProps) {
  const { t } = useTranslation();
  const n = convFilterCount(filter);
  const setFacet = (facet: ConvFacet, vals: string[]) =>
    onChange({ facets: { ...filter.facets, [facet]: vals.length ? vals : undefined } });

  return (
    <Box
      sx={{
        position: 'absolute', top: 8, right: 12, width: 340, maxHeight: 'calc(100% - 18px)', overflowY: 'auto',
        bgcolor: 'background.paper', borderRadius: 3, boxShadow: '0 16px 40px rgba(0,0,0,.16)', zIndex: 10, p: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ fontSize: 13.5, fontWeight: 700, flex: 1 }}>{t('promptStudio.conv.filters.title')}</Box>
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{t('promptStudio.conv.filters.count', { count: personas.length })}</Box>
        {n > 0 && (
          <Box component="button" onClick={() => onChange({ facets: {} })}
            sx={{ fontSize: 12, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0 }}>
            {t('promptStudio.conv.clearAll')}
          </Box>
        )}
        <Box component="button" onClick={onClose} title={t('common.close')}
          sx={{ fontSize: 14, border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: 'text.secondary', p: 0, lineHeight: 1 }}>
          ✕
        </Box>
      </Box>
      {CONV_FACETS.map((facet) => {
        const options = [...new Set(personas.flatMap((p) => values(p)[facet]))];
        if (options.length === 0) return null;
        options.sort((a, b) => labelOf(facet, a).localeCompare(labelOf(facet, b), 'fr'));
        return (
          <FacetSection
            key={facet}
            facet={facet}
            label={t(`promptStudio.conv.facet.${facet}`)}
            options={options}
            counts={convFacetCounts(personas, q, filter, facet, values, searchText)}
            selected={filter.facets[facet] ?? []}
            onChange={(vals) => setFacet(facet, vals)}
            labelFor={(v) => labelOf(facet, v)}
          />
        );
      })}
    </Box>
  );
}
